import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Order,
  PartsSelectionDocument,
  PartsSelectionDocumentListItem,
  PartsSelectionDocumentListResponse,
  PartsSelectionDocumentStatus,
  PartsSelectionOfferGroup,
  PartsSelectionNeedItem,
  PartsSelectionNeedOeItem,
  PartsSelectionOfferItem,
  PartsSelectionOfferWarehouseAllocation
} from 'src/typings';

export type PartsSelectionAvailabilityKind = 'LOCAL' | 'OTHER_LOCAL' | 'IN_CITY' | 'OUT_OF_CITY';

export interface PartsSelectionOfferPayload {
  id?: number;
  product_id: number;
  provider_id?: number | null;
  price: number;
  quantity: number;
  product_description_snapshot: string;
  brand_snapshot: string;
  article_snapshot?: string | null;
  image_snapshot?: string | null;
  purchase_price_snapshot?: number | null;
  availability_snapshot?: string | null;
  availability_kind_snapshot?: PartsSelectionAvailabilityKind | null;
  delivery_eta_label_snapshot?: string | null;
  brand_rating_snapshot?: number | null;
  supplier_allocations?: PartsSelectionOfferSupplierAllocationPayload[];
  warehouse_allocations?: PartsSelectionOfferWarehouseAllocation[];
}

export interface PartsSelectionOfferSupplierAllocationPayload {
  id?: number;
  provider_id: number;
  contract_id?: number | null;
  quantity: number;
  expected_at?: string | null;
  expected_date_is_estimated?: boolean;
  expected_time_is_estimated?: boolean;
  purchase_price_snapshot?: number | null;
  purchase_price_source?: 'PRICE_LIST' | 'MANUAL' | null;
  availability_snapshot?: string | null;
  availability_kind_snapshot?: Exclude<PartsSelectionAvailabilityKind, 'LOCAL'> | null;
  delivery_eta_label_snapshot?: string | null;
}

export interface PartsSelectionOfferGroupPayload {
  id?: number;
  position: number;
  label: string;
  is_primary: boolean;
  items: PartsSelectionOfferPayload[];
}

export interface PartsSelectionNeedPayload {
  id?: number;
  position: number;
  need_text: string;
  quantity: number;
  oe_number?: string | null;
  oe_items: PartsSelectionNeedOePayload[];
  note?: string | null;
  offer_groups: PartsSelectionOfferGroupPayload[];
  learning_run_id?: number | null;
}

export interface PartsSelectionNeedOePayload {
  id?: number;
  position: number;
  description?: string | null;
  oe_number: string;
  catalog_source?: 'LAXIMO' | 'TECDOC' | null;
  number_kind?: 'OE' | 'ARTICLE' | null;
  brand?: string | null;
}

export interface PartsSelectionSavePayload {
  id?: number;
  client_id?: number | null;
  client_car_id?: number | null;
  phone_snapshot?: string | null;
  vin_snapshot?: string | null;
  car_description_snapshot?: string | null;
  tecdoc_linkage_target_id?: number | null;
  tecdoc_vehicle_description?: string | null;
  comment?: string | null;
  items: PartsSelectionNeedPayload[];
}

export interface PartsSelectionNeedSuggestionsResponse {
  items: string[];
}

export interface PartsSelectionListParams {
  query?: string | null;
  status?: PartsSelectionDocumentStatus | null;
  manager_user_id?: number | null;
  date_from?: string | null;
  date_to?: string | null;
  limit?: number;
  offset?: number;
}

export interface PartsSelectionCreateOrderPayload {
  need_item_ids: number[];
  warehouse_id?: number;
}

export interface PartsSelectionShareLink {
  id: number;
  url: string;
  expires_at: Date;
}

export interface PartsSelectionMatchByOePayload {
  need_text?: string | null;
  oe_number?: string | null;
  oe_items?: Array<{
    oe_number: string;
    catalog_source?: 'LAXIMO' | 'TECDOC' | null;
    number_kind?: 'OE' | 'ARTICLE' | null;
    brand?: string | null;
  }>;
  warehouse_id?: number | null;
  client_id?: number | null;
  quantity?: number | null;
  /** Страница выдачи для кнопки «Загрузить ещё». */
  page?: number | null;
  /** VIN: включает подмешивание аналогов по применимости к автомобилю. */
  vin?: string | null;
}

export interface PartsSelectionMatchItem {
  /** OE — подтверждено кроссами на введённые номера; TECDOC_VEHICLE — по применимости к авто. */
  match_source?: 'OE' | 'TECDOC_VEHICLE';
  product_id: number;
  provider_id?: number | null;
  provider_name?: string | null;
  price: number;
  quantity: number;
  product_description_snapshot: string;
  brand_snapshot: string;
  article_snapshot?: string | null;
  image_snapshot?: string | null;
  purchase_price_snapshot?: number | null;
  availability_snapshot?: string | null;
  availability_kind_snapshot?: PartsSelectionAvailabilityKind | null;
  brand_rating_snapshot?: number | null;
  delivery_eta_minutes?: number | null;
  delivery_eta_label?: string | null;
  arrival_day_diff?: number | null;
  expected_at?: string | null;
  cheapest_price?: number | null;
  cheapest_purchase_price?: number | null;
  cheapest_provider_id?: number | null;
  cheapest_provider_name?: string | null;
  cheapest_availability_snapshot?: string | null;
  /** «Поставщик, склад» самого дешёвого предложения. */
  cheapest_location?: string | null;
  cheapest_availability_kind?: PartsSelectionAvailabilityKind | null;
  cheapest_arrival_label?: string | null;
  cheapest_is_selected?: boolean;
}

export interface PartsSelectionMatchGroup {
  label: string;
  is_primary: boolean;
  items: PartsSelectionMatchItem[];
}

export interface PartsSelectionMatchResponse {
  groups: PartsSelectionMatchGroup[];
  tecdoc_unavailable: boolean;
  has_more: boolean;
  oe_set_fallback: boolean;
}

export interface PartsSelectionWarehouseOption {
  product_id: number;
  warehouse_id: number;
  warehouse_name: string;
  on_hand: number;
  reserved: number;
  available: number;
  is_target: boolean;
  has_route: boolean;
  purchase_price_snapshot: number | null;
}

export interface PartsSelectionWarehouseOptionsResponse {
  checked_at: string;
  items: PartsSelectionWarehouseOption[];
}

export interface PartsSelectionOfferSupplierOptionsPayload {
  prefer_fast_pickup?: boolean;
  selection_policy?: 'PRICE_BY_CITY';
  product_id: number;
  warehouse_id?: number | null;
  client_id?: number | null;
  quantity?: number | null;
  prefer_city_stock?: boolean;
}

export interface PartsSelectionOfferSupplierOption {
  provider_id: number | null;
  provider_name: string | null;
  provider_warehouse_id?: number | null;
  quantity: number;
  quantity_label: string | null;
  purchase_price: number | null;
  sale_price: number | null;
  availability_snapshot: string | null;
  availability_kind: PartsSelectionAvailabilityKind | null;
  arrival_label: string | null;
  arrival_day_diff: number | null;
  expected_at: string | null;
  covers_need: boolean;
}

export interface PartsSelectionOfferSupplierOptionsResponse {
  options: PartsSelectionOfferSupplierOption[];
}

export interface PartsSelectionOeSuggestionPayload {
  vin?: string | null;
  need_text?: string | null;
  warehouse_id?: number | null;
  client_id?: number | null;
  document_id?: number | null;
  need_item_id?: number | null;
  client_run_id?: string | null;
  prompt_version?: string | null;
}

export type PartsSelectionOeSuggestionStatus =
  | 'OK'
  | 'VIN_REQUIRED'
  | 'VEHICLE_NOT_FOUND'
  | 'NO_CANDIDATES'
  | 'NEED_TEXT_REQUIRED'
  | 'AI_UNAVAILABLE';

export interface PartsSelectionOeSuggestionCandidate {
  oem: string;
  name: string;
  unit: string | null;
  note: string | null;
  reason: string | null;
  /** Роль детали в комплекте: «ремень», «ролик натяжной», «помпа». */
  role: string | null;
  /** Текст потребности для отдельной строки — заполняется при mode = SPLIT. */
  need_text: string | null;
  /** Позиция на схеме Laximo, которой подтверждён выбор. */
  scheme_position: string | null;
  /** Почему расположение на схеме соответствует ремонтной потребности. */
  scheme_evidence: string | null;
  /** LAXIMO — VIN-каталог; TECDOC — кроссы; FRENKIT — комплект по OEM суппорта. */
  source: 'LAXIMO' | 'TECDOC' | 'FRENKIT';
}

export interface PartsSelectionFluidSource {
  title: string;
  url: string | null;
}

export interface PartsSelectionFluidSpec {
  engine: string | null;
  capacity_liters: number | null;
  viscosity: string | null;
  specs: string[];
  sources: PartsSelectionFluidSource[];
  note: string | null;
}

export interface PartsSelectionOeSuggestionResponse {
  learning_run_id?: number | null;
  status: PartsSelectionOeSuggestionStatus;
  message: string | null;
  vehicle: { catalog: string; brand: string | null; name: string | null } | null;
  /**
   * SINGLE — деталь и её альтернативы; KIT — состав комплекта (все номера в одну потребность);
   * SPLIT — потребность разбивается на несколько строк, по одной на деталь.
   */
  mode: 'SINGLE' | 'KIT' | 'SPLIT' | 'FLUID';
  /**
   * Есть ли чем заполнить потребность. Считает бэкенд: носитель результата у каждого режима
   * свой, и проверка «по режиму» на фронте трижды теряла исправный ответ.
   */
  has_result: boolean;
  best: PartsSelectionOeSuggestionCandidate | null;
  alternatives: PartsSelectionOeSuggestionCandidate[];
  components: PartsSelectionOeSuggestionCandidate[];
  /** mode = FLUID: требования к жидкости по двигателю. */
  fluid: PartsSelectionFluidSpec | null;
  /** mode = FLUID: готовые предложения из нашего каталога. */
  fluid_offers: PartsSelectionMatchGroup[];
}

export interface PartsSelectionLearningBaselinePayload {
  oe_items: Array<{ oe_number: string; description?: string | null }>;
  offer_groups: Array<{
    label: string;
    is_primary: boolean;
    items: Array<{
      product_id: number;
      provider_id?: number | null;
      price: number;
      quantity: number;
      article?: string | null;
      brand?: string | null;
    }>;
  }>;
}

export interface PartsSelectionOfferCheapestPayload {
  product_ids: number[];
  warehouse_id?: number | null;
  client_id?: number | null;
  quantity?: number | null;
}

export interface PartsSelectionOfferCheapestItem {
  product_id: number;
  cheapest_price: number | null;
  cheapest_purchase_price: number | null;
  cheapest_provider_id: number | null;
  cheapest_provider_name: string | null;
  cheapest_location: string | null;
  cheapest_availability_snapshot: string | null;
  cheapest_availability_kind: PartsSelectionAvailabilityKind | null;
  cheapest_arrival_label: string | null;
}

export interface PartsSelectionOfferCheapestResponse {
  items: PartsSelectionOfferCheapestItem[];
}

export interface PartsSelectionOfferPurchasePricePayload {
  product_id: number;
  provider_id?: number | null;
  warehouse_id?: number | null;
  client_id?: number | null;
  quantity?: number | null;
}

export interface PartsSelectionOfferPurchasePriceResponse {
  purchase_price_snapshot: number | null;
  availability_snapshot: string | null;
  availability_kind_snapshot: PartsSelectionAvailabilityKind | null;
  sale_price?: number | null;
}

export interface PartsSelectionSupplierOeDebugPayload {
  oe_number: string;
  brand?: string | null;
  contract_id?: number | null;
}

export interface PartsSelectionSupplierOeDebugSourceResponse {
  contractId: number;
  providerCode: string;
  providerName: string;
  contractNumber?: string | null;
  status: 'SUCCESS' | 'FAILED';
  durationMs: number;
  error?: string;
  rawRowCount: number;
  filteredRowCount: number;
  groupCount: number;
  rawRows: unknown[];
  rows: unknown[];
  groups: unknown[];
}

export interface PartsSelectionSupplierOeDebugResponse {
  persisted: false;
  article: string;
  brand?: string | null;
  normalizedArticle: string;
  normalizedBrand?: string | null;
  selectedContractId?: number | null;
  sourceCount: number;
  totalRawRowCount: number;
  totalFilteredRowCount: number;
  durationMs: number;
  sources: PartsSelectionSupplierOeDebugSourceResponse[];
}

export interface PartsSelectionCreateOrderResponse {
  order: Order;
  document: PartsSelectionDocument;
  reused_order?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PartsSelectionService {
  constructor(private readonly http: HttpClient) {}

  getList(params: PartsSelectionListParams = {}): Observable<PartsSelectionDocumentListResponse> {
    const payload = Object.entries({
      query: params.query || undefined,
      status: params.status || undefined,
      manager_user_id: params.manager_user_id || undefined,
      date_from: params.date_from || undefined,
      date_to: params.date_to || undefined,
      limit: params.limit ?? 20,
      offset: params.offset ?? 0
    }).reduce<Record<string, string | number>>((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }

      return acc;
    }, {});

    return this.http
      .get<PartsSelectionDocumentListResponse>(`${environment.BACKEND}/parts-selection`, {
        params: payload
      })
      .pipe(
        map((response) => ({
          ...response,
          items: (response.items || []).map((document: PartsSelectionDocumentListItem) => ({
            ...document,
            date_created: new Date(document.date_created),
            date_updated: new Date(document.date_updated)
          }))
        }))
      );
  }

  create(payload: PartsSelectionSavePayload): Observable<PartsSelectionDocument> {
    return this.http
      .post<PartsSelectionDocument>(`${environment.BACKEND}/parts-selection/create`, payload)
      .pipe(map((document) => this.mapDocument(document)));
  }

  save(payload: PartsSelectionSavePayload): Observable<PartsSelectionDocument> {
    return this.http
      .put<PartsSelectionDocument>(`${environment.BACKEND}/parts-selection/save`, payload)
      .pipe(map((document) => this.mapDocument(document)));
  }

  resolveOfferWarehouseOptions(productIds: number[], targetWarehouseId: number) {
    return this.http.post<PartsSelectionWarehouseOptionsResponse>(
      `${environment.BACKEND}/parts-selection/offer-warehouse-options`,
      { product_ids: productIds, target_warehouse_id: targetWarehouseId }
    );
  }

  getOne(id: number): Observable<PartsSelectionDocument> {
    return this.http
      .get<PartsSelectionDocument>(`${environment.BACKEND}/parts-selection/${id}`)
      .pipe(map((document) => this.mapDocument(document)));
  }

  getNeedSuggestions(query: string, limit = 10): Observable<PartsSelectionNeedSuggestionsResponse> {
    return this.http.post<PartsSelectionNeedSuggestionsResponse>(
      `${environment.BACKEND}/parts-selection/need-suggestions`,
      {
        query,
        limit
      }
    );
  }

  getNeedOeSuggestions(
    query: string,
    limit = 10
  ): Observable<PartsSelectionNeedSuggestionsResponse> {
    return this.http.post<PartsSelectionNeedSuggestionsResponse>(
      `${environment.BACKEND}/parts-selection/need-oe-suggestions`,
      {
        query,
        limit
      }
    );
  }

  matchByOe(payload: PartsSelectionMatchByOePayload): Observable<PartsSelectionMatchResponse> {
    return this.http.post<PartsSelectionMatchResponse>(
      `${environment.BACKEND}/parts-selection/match-by-oe`,
      payload
    );
  }

  resolveOfferSupplierOptions(
    payload: PartsSelectionOfferSupplierOptionsPayload
  ): Observable<PartsSelectionOfferSupplierOptionsResponse> {
    return this.http.post<PartsSelectionOfferSupplierOptionsResponse>(
      `${environment.BACKEND}/parts-selection/offer-supplier-options`,
      payload
    );
  }

  /** Самое дешёвое предложение сразу по пачке карточек — для бейджей открытого документа. */
  resolveOfferCheapestOptions(
    payload: PartsSelectionOfferCheapestPayload
  ): Observable<PartsSelectionOfferCheapestResponse> {
    return this.http.post<PartsSelectionOfferCheapestResponse>(
      `${environment.BACKEND}/parts-selection/offer-cheapest`,
      payload
    );
  }

  /** Подсказка OE-номера по VIN через каталог Laximo + ИИ. */
  suggestOe(
    payload: PartsSelectionOeSuggestionPayload
  ): Observable<PartsSelectionOeSuggestionResponse> {
    return this.http.post<PartsSelectionOeSuggestionResponse>(
      `${environment.BACKEND}/parts-selection/suggest-oe`,
      payload
    );
  }

  saveLearningBaseline(runId: number, payload: PartsSelectionLearningBaselinePayload) {
    return this.http.put<void>(
      `${environment.BACKEND}/parts-selection/learning/runs/${runId}/baseline`,
      payload
    );
  }

  resolveOfferPurchasePrice(
    payload: PartsSelectionOfferPurchasePricePayload
  ): Observable<PartsSelectionOfferPurchasePriceResponse> {
    return this.http.post<PartsSelectionOfferPurchasePriceResponse>(
      `${environment.BACKEND}/parts-selection/offer-purchase-price`,
      payload
    );
  }

  debugSupplierOeLookup(
    payload: PartsSelectionSupplierOeDebugPayload
  ): Observable<PartsSelectionSupplierOeDebugResponse> {
    return this.http.post<PartsSelectionSupplierOeDebugResponse>(
      `${environment.BACKEND}/parts-selection/debug/supplier-oe-lookup`,
      payload
    );
  }

  createOrder(
    id: number,
    payload: PartsSelectionCreateOrderPayload
  ): Observable<PartsSelectionCreateOrderResponse> {
    return this.http
      .post<PartsSelectionCreateOrderResponse>(
        `${environment.BACKEND}/parts-selection/${id}/create-order`,
        payload
      )
      .pipe(
        map((response) => ({
          ...response,
          order: {
            ...response.order,
            date_created: new Date(response.order.date_created),
            date_expected: new Date(response.order.date_expected)
          },
          document: this.mapDocument(response.document)
        }))
      );
  }

  createSale(
    id: number,
    payload: PartsSelectionCreateOrderPayload
  ): Observable<PartsSelectionCreateOrderResponse> {
    return this.http
      .post<PartsSelectionCreateOrderResponse>(
        `${environment.BACKEND}/parts-selection/${id}/create-sale`,
        payload
      )
      .pipe(
        map((response) => ({
          ...response,
          order: {
            ...response.order,
            date_created: new Date(response.order.date_created),
            date_expected: new Date(response.order.date_expected)
          },
          document: this.mapDocument(response.document)
        }))
      );
  }

  createShareLink(id: number, expiresInDays = 7): Observable<PartsSelectionShareLink> {
    return this.http
      .post<{ id: number; url: string; expires_at: string }>(
        `${environment.BACKEND}/parts-selection/${id}/share-links`,
        { expires_in_days: expiresInDays }
      )
      .pipe(
        map((response) => ({
          ...response,
          expires_at: new Date(response.expires_at)
        }))
      );
  }

  private mapDocument(document: PartsSelectionDocument): PartsSelectionDocument {
    return {
      ...document,
      date_created: new Date(document.date_created),
      date_updated: new Date(document.date_updated),
      client: document.client
        ? {
            ...document.client,
            phones: (document.client.phones || []).map((phone) => ({
              ...phone,
              createdAt: phone.createdAt ? new Date(phone.createdAt) : phone.createdAt
            }))
          }
        : null,
      client_car: document.client_car || null,
      need_items: (document.need_items || []).map((item: PartsSelectionNeedItem) => ({
        ...item,
        quantity: Number(item.quantity || 0),
        converted_at: item.converted_at ? new Date(item.converted_at) : null,
        oe_items: (item.oe_items || []).map((oeItem: PartsSelectionNeedOeItem) => ({
          ...oeItem,
          position: Number(oeItem.position || 0)
        })),
        offer_groups: (item.offer_groups || []).map((group: PartsSelectionOfferGroup) => ({
          ...group,
          items: (group.items || []).map((offer: PartsSelectionOfferItem) => ({
            ...offer,
            price: Number(offer.price || 0),
            quantity: Number(offer.quantity || 0),
            purchase_price_snapshot:
              offer.purchase_price_snapshot !== null && offer.purchase_price_snapshot !== undefined
                ? Number(offer.purchase_price_snapshot)
                : null,
            availability_kind_snapshot: offer.availability_kind_snapshot || null,
            brand_rating_snapshot:
              offer.brand_rating_snapshot !== null && offer.brand_rating_snapshot !== undefined
                ? Number(offer.brand_rating_snapshot)
                : null,
            warehouse_allocations: (offer.warehouse_allocations || []).map((allocation) => ({
              ...allocation,
              warehouse_id: Number(allocation.warehouse_id),
              quantity: Number(allocation.quantity),
              purchase_price_snapshot:
                allocation.purchase_price_snapshot == null
                  ? null
                  : Number(allocation.purchase_price_snapshot)
            })),
            supplier_allocations: (offer.supplier_allocations || []).map((allocation) => ({
              ...allocation,
              quantity: Number(allocation.quantity || 0),
              expected_at: allocation.expected_at || null,
              purchase_price_snapshot:
                allocation.purchase_price_snapshot !== null &&
                allocation.purchase_price_snapshot !== undefined
                  ? Number(allocation.purchase_price_snapshot)
                  : null
            }))
          }))
        }))
      })),
      created_orders: (document.created_orders || []).map((order) => ({
        ...order,
        date_created: order.date_created ? new Date(order.date_created) : null
      }))
    };
  }
}
